import { lazy, Suspense } from "react";
import { Adjuntos, Alerta, Button, Card, Deslizador, Row, Select, Spinner, Stack } from "@/components";
import { TIPOS_IMAGEN } from "@/lib/adjuntos";
import { useRecorridoTab } from "./useRecorridoTab";

// three.js pesa: el visor se baja recién al entrar al recorrido.
const Inmersivo3D = lazy(() => import("@/components/Inmersivo3D"));
const Panorama360 = lazy(() => import("@/components/Panorama360"));
const CARGANDO = <p className="grid h-[70vh] place-items-center rounded-control bg-superficie-2 text-chico text-texto-suave">Preparando el recorrido…</p>;

/** Recorrido inmersivo de una unidad (Mati, 2026-11-01): adentro del depto, con el sol de la hora y las terminaciones. */
export function RecorridoTab() {
  const t = useRecorridoTab();
  return (
    <Stack espacio={4}>
      <Row className="items-center justify-between gap-2">
        <h1 className="text-pantalla font-light tracking-[-0.03em]">{t.titulo}</h1>
        <Button variante="fantasma" tamanio="sm" onClick={t.volver}>
          Volver a Proyecto
        </Button>
      </Row>
      {t.cargando && <Spinner />}
      {t.error && <Alerta texto={t.error} accion={<Button variante="fantasma" tamanio="sm" onClick={t.reintentar}>Reintentar</Button>} />}
      {t.datos && (
        <>
          {t.hayFotos && (
            <Row espacio={2} envolver role="group" aria-label="Cómo verlo">
              <Button variante={t.modo === "foto" ? "primario" : "secundario"} tamanio="sm" aria-pressed={t.modo === "foto"} onClick={() => t.elegirModo("foto")}>
                Foto realista
              </Button>
              <Button variante={t.modo === "maqueta" ? "primario" : "secundario"} tamanio="sm" aria-pressed={t.modo === "maqueta"} onClick={() => t.elegirModo("maqueta")}>
                Recorrer la maqueta
              </Button>
            </Row>
          )}
          {t.modo === "foto" && t.urlFoto && (
            <>
              <Suspense fallback={CARGANDO}>
                <Panorama360 etiqueta={`Foto 360° de ${t.nombreFoto} en ${t.titulo}`} url={t.urlFoto} />
              </Suspense>
              {t.fotos.length > 1 && (
                <Row espacio={2} envolver role="group" aria-label="Ambientes">
                  {t.fotos.map((f) => (
                    <Button key={f.id} variante={f.id === t.fotoId ? "primario" : "secundario"} tamanio="sm" aria-pressed={f.id === t.fotoId} onClick={() => t.elegirFoto(f.id)}>
                      {f.nombre}
                    </Button>
                  ))}
                </Row>
              )}
              <p className="text-chico text-texto-suave">Arrastrá para mirar alrededor (o tocá "Mirar moviendo el celular") y acercá con dos dedos.</p>
            </>
          )}
          {t.modo === "maqueta" && (
            <>
              <Suspense fallback={CARGANDO}>
                <Inmersivo3D etiqueta={`Recorrido por dentro de ${t.titulo}`} datos={t.datos} sol={t.sol} alturaSol={t.alturaSol} piso={t.piso} paredes={t.paredes} />
              </Suspense>
              <p className="text-chico text-texto-suave">
                Arrastrá para mirar alrededor y tocá el piso para caminar (en la compu, también con las flechas). Abajo, los ambientes para ir directo.
              </p>
              <Card>
                <Stack espacio={3}>
                  <h2 className="text-titulo font-light tracking-tight">El sol</h2>
                  <Select etiqueta="Época del año" opciones={t.estaciones} valor={t.estacion} onCambio={t.elegirEstacion} />
                  <Deslizador etiqueta="Hora" valor={t.hora} min={6} max={21} paso={0.25} onCambio={t.elegirHora} formato={t.formatoHora} ayuda={t.resumenSol} />
                </Stack>
              </Card>
              <Card>
                <Stack espacio={3}>
                  <h2 className="text-titulo font-light tracking-tight">Terminaciones</h2>
                  <Select etiqueta="Piso" opciones={t.acabadosPiso} valor={t.piso} onCambio={t.elegirPiso} />
                  <Select etiqueta="Paredes" opciones={t.coloresPared} valor={t.paredes} onCambio={t.elegirParedes} />
                </Stack>
              </Card>
            </>
          )}
          {t.esAdmin && (
            <Card>
              <Stack espacio={3}>
                <h2 className="text-titulo font-light tracking-tight">Fotos 360° del recorrido</h2>
                <p className="text-chico text-texto-suave">
                  Una foto 360° por ambiente (JPG de 2:1, como las que calcula Blender). El nombre del archivo es el del ambiente: “living.jpg”,
                  “vista-desde-el-balcon.jpg”. Con al menos una, el recorrido arranca en la foto realista.
                </p>
                <Adjuntos
                  etiqueta="Fotos 360°"
                  adjuntos={t.archivosFotos}
                  rutaDescarga={t.rutaDescarga}
                  puedeEditar
                  tipos={TIPOS_IMAGEN}
                  onSubir={t.subirFoto}
                  onBorrar={t.borrarFoto}
                  subiendo={t.subiendoFoto}
                  error={t.errorFotos}
                />
              </Stack>
            </Card>
          )}
          {t.esAdmin && t.modo === "maqueta" && (
            <Card>
              <Stack espacio={3}>
                <h2 className="text-titulo font-light tracking-tight">Para que el sol sea el real</h2>
                <Select etiqueta="¿Hacia dónde mira el frente del edificio?" opciones={t.rumbos} valor={t.rumbo} onCambio={(v) => void t.elegirRumbo(v)} deshabilitado={t.guardandoRumbo} />
                {t.sinRumbo && <p className="text-chico text-texto-suave">Mientras no lo cargues, el sol se calcula como si el frente mirara al norte.</p>}
                {t.sinUbicacion && <p className="text-chico text-texto-suave">Cargá la dirección del terreno para usar su ubicación exacta (por ahora, el centro de Buenos Aires).</p>}
                <Alerta texto={t.errorRumbo} />
              </Stack>
            </Card>
          )}
          <p className="text-chico text-texto-suave">
            {t.modo === "foto"
              ? "Imagen ilustrativa calculada por computadora: puede tener diferencias con la obra terminada."
              : "Imagen ilustrativa: la distribución y los muebles son de ejemplo hasta tener los planos del arquitecto."}
          </p>
        </>
      )}
    </Stack>
  );
}
